import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ParkingSpotsPage() {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ identifier: "", address: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/parking-spots`);
      setSpots(data);
    } catch (e) { if (e.response?.status !== 401) toast.error("Error cargando plazas"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ identifier: "", address: "" });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ identifier: s.identifier, address: s.address });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/parking-spots/${editing.id}`, form);
        toast.success("Plaza actualizada");
      } else {
        await axios.post(`${API}/parking-spots`, form);
        toast.success("Plaza creada");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta plaza?")) return;
    try {
      await axios.delete(`${API}/parking-spots/${id}`);
      toast.success("Plaza eliminada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="parking-spots-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Plazas de garaje</h2>
          <p className="text-sm text-gray-500 mt-1">{spots.length} plazas registradas</p>
        </div>
        <button onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 flex items-center gap-2" data-testid="add-spot-button">
          <Plus className="w-4 h-4" /> Nueva plaza
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm" data-testid="spots-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Identificador</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Direccion</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {spots.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-gray-400">No hay plazas registradas</TableCell></TableRow>
            ) : spots.map((s) => (
              <TableRow key={s.id} className="hover:bg-gray-50 transition-colors">
                <TableCell className="text-sm font-medium text-gray-900 py-4 px-4">{s.identifier}</TableCell>
                <TableCell className="text-sm text-gray-600 py-4 px-4">{s.address}</TableCell>
                <TableCell className="py-4 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(s)} className="p-1.5 text-gray-400 hover:text-[#002FA7] transition-colors" data-testid={`edit-spot-${s.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(s.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-spot-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm" data-testid="spot-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar plaza" : "Nueva plaza"}</DialogTitle>
            <DialogDescription>{editing ? "Modifica los datos de la plaza" : "Introduce los datos de la nueva plaza"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Identificador</Label>
              <Input value={form.identifier} onChange={e => setForm({...form, identifier: e.target.value})} placeholder="Ej: Plaza 1, B-05" className="rounded-sm" data-testid="spot-identifier-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Direccion</Label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Calle, numero, ciudad, CP" className="rounded-sm" data-testid="spot-address-input" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="spot-cancel-button">Cancelar</button>
            <button onClick={handleSave} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="spot-save-button">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
